import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './Popover.module.css';

/** Padding from the viewport edge when clamping / flipping (px). */
const VIEWPORT_PAD = 8;

/**
 * Compute fixed coordinates for the panel given trigger + optional panel
 * measurements. Defaults to opening below the trigger; flips above when
 * `flip` is true and there is not enough room beneath (viewport-aware).
 */
function computePopoverPosition({
  triggerEl,
  panelEl,
  placement,
  offset,
  minWidthPx,
  flip,
}) {
  const rect = triggerEl.getBoundingClientRect();
  const computedMinWidth = Math.max(rect.width, minWidthPx);
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const measuredH = panelEl?.offsetHeight;
  const measuredW = panelEl?.offsetWidth;
  const panelHeight = measuredH ?? 280;
  const panelWidth = measuredW ?? computedMinWidth;

  // ── Horizontal (side) placements — used by cascading submenus. The panel
  // opens to the right/left of the trigger, top-aligned; flips to the opposite
  // side when it would overflow the viewport horizontally. ──
  const isHorizontal = placement === 'right-start' || placement === 'left-start';
  if (isHorizontal) {
    const preferRight = placement === 'right-start';
    let sideLeft = preferRight ? rect.right + offset : rect.left - offset - panelWidth;
    if (flip) {
      const overflowsRight = sideLeft + panelWidth > vw - VIEWPORT_PAD;
      const overflowsLeft = sideLeft < VIEWPORT_PAD;
      if (preferRight && overflowsRight) {
        sideLeft = rect.left - offset - panelWidth;
      } else if (!preferRight && overflowsLeft) {
        sideLeft = rect.right + offset;
      }
    }
    const sideLeftClamped = Math.max(
      VIEWPORT_PAD,
      Math.min(sideLeft, vw - panelWidth - VIEWPORT_PAD)
    );
    const sideTop = Math.max(
      VIEWPORT_PAD,
      Math.min(rect.top, vh - panelHeight - VIEWPORT_PAD)
    );
    return { top: sideTop, left: sideLeftClamped, minWidth: computedMinWidth };
  }

  const alignEnd =
    placement === 'bottom-end' ||
    placement === 'top-end';
  const alignCenter =
    placement === 'bottom-center' ||
    placement === 'top-center';

  let left = alignCenter
    ? rect.left + rect.width / 2 - panelWidth / 2
    : alignEnd
      ? rect.right - computedMinWidth
      : rect.left;

  let top;
  const preferredBelow =
    placement === 'bottom-start' ||
    placement === 'bottom-end' ||
    placement === 'bottom-center';

  if (preferredBelow) {
    top = rect.bottom + offset;
    const overflowsBottom = top + panelHeight > vh - VIEWPORT_PAD;
    if (flip && overflowsBottom) {
      const aboveTop = rect.top - offset - panelHeight;
      if (aboveTop >= VIEWPORT_PAD) {
        top = aboveTop;
      } else {
        top = Math.max(VIEWPORT_PAD, Math.min(top, vh - VIEWPORT_PAD - panelHeight));
      }
    }
  } else {
    top = rect.top - offset - panelHeight;
    const overflowsTop = top < VIEWPORT_PAD;
    if (flip && overflowsTop) {
      const belowTop = rect.bottom + offset;
      if (belowTop + panelHeight <= vh - VIEWPORT_PAD) {
        top = belowTop;
      } else {
        top = Math.max(VIEWPORT_PAD, Math.min(top, vh - VIEWPORT_PAD - panelHeight));
      }
    }
  }

  const effectiveWidth = Math.max(computedMinWidth, panelWidth);
  left = Math.max(
    VIEWPORT_PAD,
    Math.min(left, vw - effectiveWidth - VIEWPORT_PAD)
  );

  top = Math.max(
    VIEWPORT_PAD,
    Math.min(top, vh - panelHeight - VIEWPORT_PAD)
  );

  return {
    top,
    left,
    minWidth: computedMinWidth,
  };
}

/**
 * Generic anchored Popover primitive.
 *
 * Owns all chrome: portal rendering (escapes parent overflow:hidden),
 * fixed positioning anchored to a trigger ref, click-outside detection,
 * scroll/resize repositioning, fade-in animation, and the predefined
 * header layouts (title-with-Clear, search input, or fully custom).
 *
 * Ported verbatim from Themis (`cybpass-themis-agent/frontend/src/
 * components/ui/Popover/Popover.jsx`) in Epic 11.1 as a peer primitive
 * to DataTable (the FilterDrawer companion + RowActionsMenu build on it).
 *
 * Props:
 *   isOpen        — boolean; controlled open state
 *   triggerRef    — ref to the trigger element used for positioning
 *   onClose       — called on click-outside, Escape key (close requests)
 *   header        — optional discriminated union (see header config below)
 *   minWidth      — px (default = max(triggerWidth, 200))
 *   maxWidth      — px (default = null, no cap)
 *   placement     — 'bottom-start' (default), 'bottom-end', 'bottom-center',
 *                   'top-start', 'top-end', 'top-center', 'right-start',
 *                   'left-start' (side placements for submenus)
 *   offset        — px gap between trigger bottom and popover top (default 4)
 *   flip          — when true (default), flip to stay inside the viewport if the
 *                   preferred side would overflow (bottom ⇄ top).
 *   closeOnEscape — default true
 *   className     — extra wrapper class
 *   children      — body content; rendered inside the scrollable card
 *
 * Header config (discriminated union):
 *   { kind: 'title',  title, onClear? }
 *   { kind: 'search', value, onChange, placeholder?, autoFocus? }
 *   { kind: 'custom', render: () => ReactNode }
 */
const Popover = ({
  isOpen,
  triggerRef,
  onClose,
  header,
  minWidth,
  maxWidth = null,
  placement = 'bottom-start',
  offset = 4,
  flip = true,
  closeOnEscape = true,
  className = '',
  children,
}) => {
  const [pos, setPos] = useState(null); // { top, left, minWidth }
  const panelRef = useRef(null);

  const minWidthPx = typeof minWidth === 'number' ? minWidth : 200;

  // Compute position from the trigger (+ measured panel when present).
  // useLayoutEffect + optional ResizeObserver keeps menus visible near the
  // bottom of the viewport (flip above) without clipping under pagination.
  useLayoutEffect(() => {
    if (!isOpen) {
      setPos(null);
      return undefined;
    }

    const updatePos = () => {
      const triggerEl = triggerRef?.current;
      if (!triggerEl) return;
      setPos(
        computePopoverPosition({
          triggerEl,
          panelEl: panelRef.current,
          placement,
          offset,
          minWidthPx,
          flip,
        })
      );
    };

    updatePos();

    let innerRaf;
    const outerRaf = requestAnimationFrame(() => {
      innerRaf = requestAnimationFrame(updatePos);
    });

    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);

    return () => {
      cancelAnimationFrame(outerRaf);
      if (innerRaf != null) cancelAnimationFrame(innerRaf);
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [isOpen, triggerRef, minWidthPx, placement, offset, flip]);

  // Click-outside: covers BOTH the trigger and the portal panel.
  // Without checking the trigger, clicking the trigger to toggle would
  // immediately re-close the popover via this handler.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleMouseDown = (event) => {
      const triggerEl = triggerRef?.current;
      const insideTrigger = triggerEl && triggerEl.contains(event.target);
      const insidePanel = panelRef.current && panelRef.current.contains(event.target);
      if (!insideTrigger && !insidePanel) {
        onClose?.();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen, triggerRef, onClose]);

  // Escape to close
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return undefined;
    const handleKey = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, closeOnEscape, onClose]);

  if (!isOpen || !pos) return null;

  const panelStyle = {
    top: `${pos.top}px`,
    left: `${pos.left}px`,
    minWidth: `${pos.minWidth}px`,
  };
  if (maxWidth != null) panelStyle.maxWidth = `${maxWidth}px`;

  const panel = (
    <div
      ref={panelRef}
      className={`${styles.panel} ${className}`.trim()}
      style={panelStyle}
      role="dialog"
    >
      {header && <PopoverHeader header={header} />}
      <div className={styles.body}>{children}</div>
    </div>
  );

  return createPortal(panel, document.body);
};

/* ---------- Header sub-component (discriminated-union renderer) ---------- */

const PopoverHeader = ({ header }) => {
  if (header.kind === 'title') {
    return (
      <div className={styles.titleHeader}>
        <span className={styles.titleText}>{header.title}</span>
        {header.onClear && (
          <button
            type="button"
            className={styles.clearLink}
            onClick={(e) => {
              e.stopPropagation();
              header.onClear();
            }}
          >
            Clear
          </button>
        )}
      </div>
    );
  }

  if (header.kind === 'search') {
    const autoFocus = header.autoFocus !== false; // default true
    return (
      <div className={styles.searchHeader}>
        <div className={styles.searchWrap}>
          <svg viewBox="0 0 24 24" className={styles.searchIcon} aria-hidden="true">
            <path
              d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"
              fill="currentColor"
            />
          </svg>
          <input
            type="text"
            className={styles.searchInput}
            value={header.value ?? ''}
            onChange={(e) => header.onChange?.(e.target.value)}
            placeholder={header.placeholder}
            autoFocus={autoFocus}
          />
        </div>
      </div>
    );
  }

  if (header.kind === 'custom') {
    return <div className={styles.customHeader}>{header.render()}</div>;
  }

  return null;
};

export default Popover;
